// The following code is pulled from and inspired by the `bonFHIR` implementation
// https://bonfhir.dev/packages/core/fhir-client#mergers
// https://github.com/bonfhir/bonfhir/blob/1e929e2c67734a813ad62ab0e79606cb1f7652a8/packages/core/src/r4b/merge.ts

import type { ResourceType } from '@medplum/fhirtypes'
import { cloneDeep } from 'lodash-es'
import { compositionMerger } from '#src/merge/mergers/composition-merger.ts'
import { conditionClinicalStatusMerger } from '#src/merge/mergers/condition-clinical-status-merger.ts'
import { defaultMerger } from '#src/merge/mergers/default-merger.ts'
import { elementMerger } from '#src/merge/mergers/element-merger.ts'
import {
  buildIncomingOverrideArrayMerger,
  medicationRequestDosageInstructionMerger,
  serviceRequestIncomingOverrideArrayMerger,
} from '#src/merge/mergers/incoming-override-array-merger.ts'
import type {
  MergeArgs,
  MergeResult,
  Merger,
  MergerArgs,
} from '#src/merge/mergers/merger.ts'
import { practitionerRoleIdentifierMerger } from '#src/merge/mergers/practitioner-role-identifier-merger.ts'
import { useBasedArrayMerger } from '#src/merge/mergers/use-based-array-merger.ts'

const arrayMerger = elementMerger(undefined, ['id', 'url', 'system', 'use'])

export const DEFAULT_MERGERS: Merger[] = [
  compositionMerger,
  useBasedArrayMerger,
  arrayMerger,
  defaultMerger,
]

const resourceSpecificMergersMap: Partial<Record<ResourceType, Merger[]>> = {
  ServiceRequest: [serviceRequestIncomingOverrideArrayMerger],
  Condition: [conditionClinicalStatusMerger, elementMerger('note', [])],
  Practitioner: [practitionerRoleIdentifierMerger],
  DocumentReference: [buildIncomingOverrideArrayMerger('content')],
  MedicationRequest: [medicationRequestDosageInstructionMerger],
}

/**
 * Merges two resources together.
 * @param options - The merge options
 * @param options.current - The current resource
 * @param options.incoming - The incoming resource to merge
 * @param options.mergers - Optional array of custom mergers to use
 * @returns A tuple containing the merged resource and a boolean indicating if changes were made
 *
 * The default mergers handle collisions as follows:
 * 1. For arrays (handled by arrayMerger):
 *    - If an item in the incoming array matches an item in the current array,
 *      the items are recursively merged. The match is determined by comparing
 *      the 'id', 'url', 'system', or 'use' properties of the items. When a match
 *      is found, the merge process is applied recursively to the matched items.
 *      In case of conflicts, the incoming item's properties take precedence,
 *      overwriting the corresponding properties in the current item. However,
 *      properties present only in the current item are retained if not explicitly
 *      overwritten by the incoming item.
 *    - If no match is found, the incoming item is added to the array.
 * 2. For objects (handled by defaultMerger):
 *    - Properties from the incoming object overwrite those in the current object.
 *    - For nested objects, the merge is applied recursively.
 * 3. For primitive values:
 *    - The incoming value always overwrites the current value.
 */
export function merge<T extends Record<string, unknown>, PT = Partial<T>>({
  current,
  incoming,
  mergers,
}: MergeArgs<PT>): MergeResult<PT> {
  if (current === undefined) {
    return [cloneDeep(incoming) as PT, true]
  }

  const currentClone = cloneDeep(current)

  if (incoming === undefined) {
    return [currentClone as PT, false]
  }

  const resourceSpecificMergers: Merger[] =
    current &&
    typeof current === 'object' &&
    'resourceType' in current &&
    typeof current.resourceType === 'string'
      ? (resourceSpecificMergersMap[current.resourceType as ResourceType] ?? [])
      : []

  const baseMergers = mergers ?? DEFAULT_MERGERS

  const merged = applyMergers({ current: currentClone, incoming, key: '' }, [
    ...resourceSpecificMergers,
    ...baseMergers,
  ])
  return [merged, JSON.stringify(current) !== JSON.stringify(merged)]
}

function applyMergers(args: MergerArgs, mergers: Merger[]): any {
  for (const merger of mergers) {
    if (merger.canMerge(args)) {
      return merger.merge(args, (args) => applyMergers(args, mergers))
    }
  }

  throw new Error(`No merger found for ${args.current} and ${args.incoming}`)
}
